/*
 * Business Requirement:
 * OQG3-2559: iOS browsers other than Safari cannot hand the file that
 * add-to-calendar-button generates to the Calendar app, so for them the page
 * writes the .ics itself and saves it as a plain download. The content mirrors
 * the library's output: one VEVENT per date record, all-day records as DATE
 * values, and the same title / description / location.
 *
 * Technical Detail:
 * Times are written in UTC ("...Z") so the file needs no VTIMEZONE block.
 * Wall-clock times are converted from the event zone, or from the visitor's
 * zone when the calendar config carries the library's "currentBrowser" token.
 */

const BROWSER_TIME_ZONE_TOKEN = "currentBrowser";
const FALLBACK_TIME_ZONE = "UTC";
const UID_DOMAIN = "scanned.page";
const PRODUCT_ID = "-//scanned.page//Event QR//EN";
const LINE_BREAK = "\r\n";
const CALENDAR_PLACEHOLDER = " ";

/* RFC 5545 folds content lines at 75 octets; the continuation line starts
 * with a single space. */
const MAX_LINE_OCTETS = 75;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const isFilled = (value) =>
  typeof value === "string" &&
  value.trim() !== "" &&
  value !== CALENDAR_PLACEHOLDER;

const pad2 = (value) => String(value).padStart(2, "0");

const parseDateParts = (dateValue) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((dateValue || "").trim());
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
};

const parseTimeParts = (timeValue) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec((timeValue || "").trim());
  if (!match) return null;
  return { hours: Number(match[1]), minutes: Number(match[2]) };
};

const zoneFormatterCache = new Map();

const getZoneFormatter = (timeZone) => {
  if (zoneFormatterCache.has(timeZone)) return zoneFormatterCache.get(timeZone);
  let formatter = null;
  try {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    /* Unknown IANA name: the caller falls back to UTC (offset 0). */
    formatter = null;
  }
  zoneFormatterCache.set(timeZone, formatter);
  return formatter;
};

/* Offset (ms) between the zone's wall clock and UTC at the given instant. */
const zoneOffsetMs = (timeZone, utcMs) => {
  const formatter = getZoneFormatter(timeZone);
  if (!formatter) return 0;
  const parts = {};
  formatter.formatToParts(new Date(utcMs)).forEach((part) => {
    parts[part.type] = part.value;
  });
  const wallClockAsUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second),
  );
  return wallClockAsUtc - utcMs;
};

/*
 * Convert a wall-clock date/time in the given zone to a UTC timestamp. The
 * first guess treats the wall clock as UTC; the second pass corrects for a
 * DST transition sitting between the guess and the real instant.
 */
const zonedTimeToUtcMs = (dateParts, timeParts, timeZone) => {
  const wallClockAsUtc = Date.UTC(
    dateParts.year,
    dateParts.month - 1,
    dateParts.day,
    timeParts.hours,
    timeParts.minutes,
  );
  const firstOffset = zoneOffsetMs(timeZone, wallClockAsUtc);
  const firstGuess = wallClockAsUtc - firstOffset;
  const secondOffset = zoneOffsetMs(timeZone, firstGuess);
  return secondOffset === firstOffset
    ? firstGuess
    : wallClockAsUtc - secondOffset;
};

/*
 * Move a wall-clock date and time from one IANA zone to another. Returns
 * YYYY-MM-DD and HH:mm in the target zone, or null when either value cannot
 * be parsed. The date can change when the instant crosses midnight.
 */
export const convertWallClockToZone = (
  dateValue,
  timeValue,
  fromZone,
  toZone,
) => {
  const dateParts = parseDateParts(dateValue);
  const timeParts = parseTimeParts(timeValue);
  if (!dateParts || !timeParts) return null;
  if (!getZoneFormatter(fromZone) || !getZoneFormatter(toZone)) return null;

  if (fromZone === toZone) {
    return {
      date: `${dateParts.year}-${pad2(dateParts.month)}-${pad2(dateParts.day)}`,
      time: `${pad2(timeParts.hours)}:${pad2(timeParts.minutes)}`,
    };
  }

  const parts = {};
  getZoneFormatter(toZone)
    .formatToParts(
      new Date(zonedTimeToUtcMs(dateParts, timeParts, fromZone)),
    )
    .forEach((part) => {
      parts[part.type] = part.value;
    });

  const hour = parts.hour === "24" ? "00" : parts.hour;

  return {
    date: `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`,
    time: `${pad2(hour)}:${pad2(parts.minute)}`,
  };
};

const resolveBrowserTimeZone = () => {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIME_ZONE
    );
  } catch {
    return FALLBACK_TIME_ZONE;
  }
};

export const resolveIcsTimeZone = (timeZone) => {
  if (!isFilled(timeZone) || timeZone === BROWSER_TIME_ZONE_TOKEN) {
    return resolveBrowserTimeZone();
  }
  return getZoneFormatter(timeZone) ? timeZone : resolveBrowserTimeZone();
};

const formatUtcDateTime = (utcMs) => {
  const date = new Date(utcMs);
  return (
    `${date.getUTCFullYear()}${pad2(date.getUTCMonth() + 1)}${pad2(date.getUTCDate())}` +
    `T${pad2(date.getUTCHours())}${pad2(date.getUTCMinutes())}${pad2(date.getUTCSeconds())}Z`
  );
};

const formatDateOnly = (dateParts) =>
  `${dateParts.year}${pad2(dateParts.month)}${pad2(dateParts.day)}`;

/* All-day DTEND is exclusive, so the last day is the configured end + 1. */
const formatExclusiveEndDate = (dateParts) => {
  const nextDay = new Date(
    Date.UTC(dateParts.year, dateParts.month - 1, dateParts.day) + MS_PER_DAY,
  );
  return formatDateOnly({
    year: nextDay.getUTCFullYear(),
    month: nextDay.getUTCMonth() + 1,
    day: nextDay.getUTCDate(),
  });
};

/* Text values escape "\", ";" and "," and turn line breaks into "\n". */
export const escapeIcsText = (value) =>
  (value || "")
    .replaceAll("\\", "\\\\")
    .replaceAll(";", "\\;")
    .replaceAll(",", "\\,")
    .replaceAll("\r\n", "\n")
    .replaceAll("\r", "\n")
    .replaceAll("\n", "\\n");

const utf8Octets = (codePoint) => {
  if (codePoint < 0x80) return 1;
  if (codePoint < 0x800) return 2;
  if (codePoint < 0x10000) return 3;
  return 4;
};

export const foldIcsLine = (line) => {
  const folded = [];
  let current = "";
  let currentOctets = 0;
  for (const char of line) {
    const octets = utf8Octets(char.codePointAt(0));
    /* Continuation lines carry a leading space, which counts toward the
     * limit, so they hold one octet less than the first line. */
    const limit = folded.length === 0 ? MAX_LINE_OCTETS : MAX_LINE_OCTETS - 1;
    if (currentOctets + octets > limit) {
      folded.push(current);
      current = "";
      currentOctets = 0;
    }
    current += char;
    currentOctets += octets;
  }
  folded.push(current);
  return folded.map((part, index) => (index === 0 ? part : ` ${part}`));
};

/* The calendar config carries either one top-level record or a `dates`
 * array; normalise both to a list of complete records. */
const toRecords = (calendarConfig) => {
  const records = Array.isArray(calendarConfig.dates)
    ? calendarConfig.dates
    : [calendarConfig];
  return records.filter(
    (record) =>
      record &&
      parseDateParts(record.startDate) !== null &&
      parseDateParts(record.endDate) !== null,
  );
};

const buildDateLines = (record, timeZone) => {
  const start = parseDateParts(record.startDate);
  const end = parseDateParts(record.endDate);
  const startTime = parseTimeParts(record.startTime);
  const endTime = parseTimeParts(record.endTime);
  const isAllDay = !startTime || !endTime;

  if (isAllDay) {
    return [
      `DTSTART;VALUE=DATE:${formatDateOnly(start)}`,
      `DTEND;VALUE=DATE:${formatExclusiveEndDate(end)}`,
    ];
  }

  return [
    `DTSTART:${formatUtcDateTime(zonedTimeToUtcMs(start, startTime, timeZone))}`,
    `DTEND:${formatUtcDateTime(zonedTimeToUtcMs(end, endTime, timeZone))}`,
  ];
};

const buildTextLine = (property, value) =>
  isFilled(value) ? [`${property}:${escapeIcsText(value.trim())}`] : [];

const defaultUidSeed = () => {
  const random =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `${Date.now()}-${random}`;
};

/*
 * Build the full .ics text for an add-to-calendar-button style config. Only
 * called from the click handler, never during render, so Date.now() and the
 * random UID seed cannot cause a hydration mismatch.
 */
export const buildIcsFileContent = (
  calendarConfig,
  { now = Date.now(), uidSeed = defaultUidSeed() } = {},
) => {
  const timeZone = resolveIcsTimeZone(calendarConfig.timeZone);
  const stamp = formatUtcDateTime(now);

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    `PRODID:${PRODUCT_ID}`,
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
  ];

  toRecords(calendarConfig).forEach((record, index) => {
    lines.push(
      "BEGIN:VEVENT",
      `UID:${uidSeed}-${index + 1}@${UID_DOMAIN}`,
      `DTSTAMP:${stamp}`,
      ...buildDateLines(record, timeZone),
      ...buildTextLine("SUMMARY", calendarConfig.name),
      ...buildTextLine("DESCRIPTION", calendarConfig.description),
      ...buildTextLine("LOCATION", calendarConfig.location),
      "END:VEVENT",
    );
  });

  lines.push("END:VCALENDAR");

  return lines.flatMap(foldIcsLine).join(LINE_BREAK) + LINE_BREAK;
};
